// components/TabbedDetails/renderers.tsx
import React, { ReactNode } from 'react';
import MapView from '../MapView';
import { ContentConfig } from './types';

// Type definitions
type RendererProps = {
  content: ContentConfig;
  value: any;
  data: any;
  getValue: (key: string, fromLookingFor?: boolean) => any;
};

type ContentRenderer = (props: RendererProps) => ReactNode | null;

// Registry of renderers
const contentRenderers = new Map<string, ContentRenderer>();

// Register renderers
export function registerRenderer(type: string, renderer: ContentRenderer) {
  contentRenderers.set(type, renderer);
}

export function getRenderer(type: string): ContentRenderer | undefined {
  return contentRenderers.get(type);
}

// Default renderers
const defaultRenderers: Record<string, ContentRenderer> = {
  card: ({ content, value, data, getValue }) => {
    if (!value && !content.customRender) return null;
    
    const Icon = content.icon;
    let displayValue = '';
    
    if (content.type === 'range') {
      displayValue = content.format ? content.format(value, value) : `${value?.min} - ${value?.max}`;
    } else if (content.format) {
      displayValue = content.format(value);
    } else if (content.customRender) {
      return content.customRender(value, data);
    } else {
      displayValue = String(value);
    }
    
    return (
      <div key={content.key} className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
        {Icon && <Icon className="text-green-500" size={16} />}
        <div>
          <div className="text-xs text-gray-500">{content.label}</div>
          <div className="text-sm font-medium text-gray-800">{displayValue}</div>
        </div>
      </div>
    );
  },
  
  tags: ({ content, value }) => {
    if (!value || !Array.isArray(value) || value.length === 0) return null;
    
    const Icon = content.icon;
    
    return (
      <div key={content.key} className="space-y-3">
        <div className="flex items-center gap-2">
          {Icon && <Icon className="text-pink-500" size={16} />}
          <h3 className="text-sm font-semibold text-gray-800">{content.label}</h3>
        </div>
        <div className="flex flex-wrap gap-2">
          {value.map((item: string, idx: number) => (
            <span key={idx} className="px-3 py-1.5 bg-gray-100 rounded-full text-sm text-gray-700">
              {item}
            </span>
          ))}
        </div>
      </div>
    );
  },
  
  map: ({ content, getValue }) => {
    const lat = content.latKey ? getValue(content.latKey, content.lookingFor) : null;
    const lng = content.lngKey ? getValue(content.lngKey, content.lookingFor) : null;
    
    if (!lat || !lng) return null;
    
    const mapItem = {
      id: content.key,
      title: content.label,
      lat: typeof lat === 'string' ? parseFloat(lat) : lat,
      lng: typeof lng === 'string' ? parseFloat(lng) : lng,
      label: content.label
    };
    
    return (
      <div key={content.key} className="col-span-2">
        <div className="h-64 w-full rounded-xl overflow-hidden">
          <MapView
            items={[mapItem]}
            height="100%"
            zoom={13}
            center={[mapItem.lat, mapItem.lng]}
            renderPopup={(item) => (
              <div className="p-2">
                <p className="font-semibold text-sm">{item.title}</p>
                <p className="text-xs text-gray-600">{item.label}</p>
              </div>
            )}
          />
        </div>
      </div>
    );
  },
  
  custom: ({ content, value, data }) => {
    return content.customRender?.(value, data) || null;
  }
};

// Register all default renderers
Object.entries(defaultRenderers).forEach(([type, renderer]) => {
  registerRenderer(type, renderer);
});



// // 5. Example Usage with Custom Renderer Registration

// // app.tsx or main entry point
// import { registerRenderer } from './components/TabbedDetails/renderers';

// // Register a custom video renderer for a new content type
// registerRenderer('video', ({ content, value }) => {
//   if (!value) return null;
  
//   return (
//     <div key={content.key} className="col-span-2">
//       <video controls className="w-full rounded-xl">
//         <source src={value} />
//       </video>
//     </div>
//   );
// });

// // Register a custom audio renderer
// registerRenderer('audio', ({ content, value }) => {
//   if (!value) return null;
  
//   return (
//     <div key={content.key} className="col-span-2">
//       <audio controls className="w-full">
//         <source src={value} />
//       </audio>
//     </div>
//   );
// });

// // Now you can use these types in your ContentConfig
// const customContent: ContentConfig = {
//   tab: 'about',
//   section: 'media',
//   type: 'video',  // Custom type
//   key: 'intro_video',
//   label: 'Introduction Video'
// };